import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { OccupancyIcon, OccupiedIcon, VacantIcon } from '@/Components/Dashboard/icons';
import useGetUserEventsMutation from '@/Components/Hooks/useGetUserEventsMutation';
import {
  ACCESSORIES_PROPERTIES,
  DEVICE_EVENT_TYPES,
  DEVICE_EVENTS_PROPERTY,
  DEVICE_STATE_PROPERTY,
  DEVICE_TYPES,
  EVENT_PROPERTIES,
} from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  EmptyModalContent,
  ErrorModalContent,
  formatTime,
  getDateLabel,
  LoadingCard,
  LoadingModalContent,
  sortByLatestTimestamp,
} from '../RecentActivityUtils';

const deviceTypes = [DEVICE_TYPES.OCCUPANCY_SENSOR];
const eventTypes = deviceTypes.flatMap((type) => DEVICE_EVENT_TYPES[type] || []);

const ModalContent = ({ timezone, is24HourFormat, locale, data, isLoading, isError }) => {
  const trans = useSelector(translator);
  if (isLoading) return <LoadingModalContent />;

  if (isError) return <ErrorModalContent />;

  if (isEmpty(data)) return <EmptyModalContent />;

  const rows = sortByLatestTimestamp(Array.isArray(data) ? data : []);
  return (
    <div className="recent-blood-pressure-table-card">
      <div className="recent-blood-pressure-table-head">
        <div className="head-cell head-cell-date">{trans('DATE_L')}</div>
        <div className="head-cell head-cell-bp">{trans('LOCATION')}</div>
        <div className="head-cell">{trans('STATE')}</div>
      </div>

      <div className="recent-blood-pressure-table-body">
        {rows.map((entry, index) => {
          const timestamp = get(entry, 'utc_timestamp_inms', '');
          const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
          const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
          const location = get(entry, 'location', '-');
          const state = get(entry, 'active', '');
          const stateLabel = state !== '' ? (state ? trans('OCCUPIED') : trans('VACANT')) : '-';
          const Icon = state !== '' ? (state ? OccupiedIcon : VacantIcon) : null;

          return (
            <div
              className="recent-blood-pressure-table-row"
              key={`recent-blood-pressure-table-row-${index}`}
            >
              <div className="row-cell date-cell">
                <span className="date-text">{dateLabel}</span>
                <span className="time-text">{formattedTime}</span>
              </div>

              <div className="row-cell bp-cell">{location}</div>
              <div className="row-cell bp-cell">
                {Icon && <Icon className="state-icon" />}
                {stateLabel}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ModalContent.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

const SmartHomeOccupancyCard = (props) => {
  const { useruuid, timezone, locale, is24HourFormat, accessories, data, isLoading, trendData } =
    props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();
  const modalDataApi = useGetUserEventsMutation();
  const latestModalRequestIdRef = useRef(0);
  const invalidateModalRequest = useCallback(() => (latestModalRequestIdRef.current += 1), []);

  const eventRequestMatrix = useMemo(() => {
    const requestMap = new Map();

    (accessories || [])
      .filter((a) => deviceTypes.includes(get(a, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '')))
      .forEach((a) => {
        const deviceUuid = get(a, ACCESSORIES_PROPERTIES.DEVICE_UUID, '');
        const deviceType = get(a, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '');
        if (!deviceUuid) return;

        const filteredEventTypes = eventTypes.filter((eventType) =>
          DEVICE_EVENT_TYPES[deviceType]?.includes(eventType)
        );
        if (isEmpty(filteredEventTypes)) return;

        requestMap.set(deviceUuid, {
          deviceuuid: deviceUuid,
          eventTypes: filteredEventTypes,
        });
      });

    return Array.from(requestMap.values());
  }, [accessories]);

  const latestEvent = useMemo(() => {
    if (isEmpty(data)) return {};
    const items = Array.isArray(data) ? data : [];
    const filteredData = items.filter((event) =>
      DEVICE_EVENT_TYPES[DEVICE_TYPES.OCCUPANCY_SENSOR].includes(
        get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '')
      )
    );
    const sortedData = sortByLatestTimestamp(filteredData, EVENT_PROPERTIES.TIMESTAMP);
    return sortedData[0];
  }, [data]);

  const getModalConfig = useCallback(
    (data = [], isLoading = true, isError = false, onOpen, onClose = invalidateModalRequest) => ({
      title: (
        <div className="recent-activity-modal-title">
          <OccupancyIcon className="recent-activity-modal-title-icon" />
          <span>{trans('OCCUPANCYSENSOR')}</span>
        </div>
      ),
      content: <ModalContent {...props} data={data} isLoading={isLoading} isError={isError} />,
      onOpen,
      onClose,
    }),
    [invalidateModalRequest, props, trans]
  );

  const fetchModalData = async (requestId) => {
    const startDate = moment().subtract(7, 'days').toISOString();
    const endDate = moment().toISOString();
    const shouldUpdateModal = requestId === latestModalRequestIdRef.current;

    if (isEmpty(eventRequestMatrix)) {
      if (shouldUpdateModal) {
        openDashboardModal(getModalConfig([], false, false));
      }
      return;
    }

    const commonPayload = { useruuid, startDate, endDate };

    const mutationRequests = eventRequestMatrix.map(({ deviceuuid, eventTypes }) =>
      modalDataApi.mutateAsync({
        ...commonPayload,
        deviceuuid,
        eventTypes,
      })
    );

    if (isEmpty(mutationRequests)) {
      if (shouldUpdateModal) {
        openDashboardModal(getModalConfig([], false, true));
      }
      return;
    }

    const mutationResults = await Promise.allSettled(mutationRequests);
    const canUpdateModal = requestId === latestModalRequestIdRef.current;
    if (!canUpdateModal) return;

    const mergedData = mutationResults.flatMap((result) =>
      result.status === 'fulfilled' ? result.value : []
    );
    const hasError = mutationResults.every((result) => result.status === 'rejected');
    openDashboardModal(getModalConfig(mergedData, false, hasError));
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    const requestId = latestModalRequestIdRef.current + 1;
    latestModalRequestIdRef.current = requestId;
    openDashboardModal(getModalConfig([], true, false, () => fetchModalData(requestId)));
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const requestId = latestModalRequestIdRef.current + 1;
      latestModalRequestIdRef.current = requestId;
      openDashboardModal(getModalConfig([], true, false, () => fetchModalData(requestId)));
    }
  };

  if (isLoading) return <LoadingCard />;

  const location = get(latestEvent, EVENT_PROPERTIES.LOCATION, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const value = get(
    latestEvent,
    DEVICE_STATE_PROPERTY.occupancy_sensor,
    get(
      latestEvent,
      DEVICE_STATE_PROPERTY.occupancy_sensor_ble,
      get(latestEvent, DEVICE_EVENTS_PROPERTY.accessory_state_update_occupancy_sensor_active, '')
    )
  );
  const valueLabel = value !== '' ? (value ? trans('OCCUPIED') : trans('VACANT')) : '';

  const trendPercent = get(trendData, 'trendPercent', null);
  const yesterdayCount = get(trendData, 'yesterdayCount', null);
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <OccupancyIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('OCCUPANCYSENSOR')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        {location && <p className="recent-activity-context">{location}</p>}
        <p className="recent-activity-data-value">{valueLabel}</p>
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">
            {yesterdayCount !== null
              ? `${trans('YESTERDAY_OCCUPIED')} ${yesterdayCount} ${trans('TIMES')}`
              : trans('YESTERDAY_OCCUPIED_TIMES_EMPTY')}
          </p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

SmartHomeOccupancyCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};

export default SmartHomeOccupancyCard;
